/*
 * Copyright (c) 2026, NETGEAR, Inc.
 * 350 East Plumeria, San Jose California, 95134, U.S.A.
 * All rights reserved.
 */

/**
 * PRJCBUGEN-66106: serves the legacy device-level clients call (client/v3) from the new appcom. useApi hands it
 * here while APPCOM_DEVICE_APIS_MIGRATION_ENABLED_WEB is on; the answer is mapped back to the legacy
 * `{ info, details }` envelope, so screens are unchanged. Row contract: ConnectedClientDto, NETGEAR-BUSINESS/insight-cloud#356.
 *
 * txRate and rxRate are on few rows (the index rarely carries them); absent fields stay absent.
 */

import { isRandomizedMac } from '../views/pages/clients/clientRename';

/** apiConfig key of the new endpoint. */
export const NEW_APPCOM_CLIENT_LIST_KEY = 'GET_CLIENT_LIST_DEVICE_LEVEL_NEW_APPCOM';

/** Rows per page: legacy's page (a server property, assumed 200, the screens' own fallback) and the endpoint's cap. */
export const LEGACY_PAGE_SIZE = 200;

/** `ClientSortField` of NETGEAR-BUSINESS/insight-cloud#356; any other `sortBy` is a 400. */
export const NEW_APPCOM_SORT_FIELDS = [
  'deviceName',
  'clientType',
  'connDeviceType',
  'connDeviceModel',
  'deviceModel',
  'serialNo',
  'mac',
  'ip',
  'port',
  'ssid',
  'rssi',
  'txBytes',
  'rxBytes',
  'platform',
  'connected',
  'allowOrBlock',
  'parentMac',
  'vlanId',
  'userName',
  'connStartedAt',
  'connEndedAt',
  'createdAt',
  'modifiedAt'
];

/** Legacy's order when no sort was given. */
const LEGACY_DEFAULT_SORT = { sortBy: 'createdAt', sortDirection: 'asc' };

/**
 * Screen `sortBy` -> endpoint `sortBy`. Anything else gets LEGACY_DEFAULT_SORT; that is exact for connDeviceName
 * (the AP page's associated-device column): one device's list, so every row ties.
 */
const SORT_FIELDS = {
  deviceName: 'deviceName',
  mac: 'mac',
  ip: 'ip',
  port: 'port',
  ssid: 'ssid',
  platform: 'platform',
  vlanId: 'vlanId',
  userName: 'userName',
  rssi: 'rssi',
  rxBytes: 'rxBytes',
  txBytes: 'txBytes',
  isConnected: 'connected',
  type: 'clientType',
  connType: 'clientType',
  connDeviceModel: 'connDeviceModel',
  connectionStarted: 'connStartedAt',
  connectionEnded: 'connEndedAt'
};

const NOT_APPLICABLE = 'NA';

/** Every field of ConnectedClientDto; the test fails if the mapper misses one. */
export const CONTRACT_ROW_FIELDS = [
  'deviceName',
  'customName',
  'clientType',
  'connDeviceType',
  'connDeviceModel',
  'deviceModel',
  'serialNo',
  'mac',
  'ip',
  'port',
  'ssid',
  'rssi',
  'txBytes',
  'rxBytes',
  'platform',
  'connected',
  'allowOrBlock',
  'parentMac',
  'vlanId',
  'userName',
  'connStartedAt',
  'connEndedAt',
  'createdAt',
  'modifiedAt',
  'connDeviceName',
  'signalStrength',
  'connBand',
  'isTriBand',
  'mode',
  'bssid',
  'authentication',
  'encryption',
  'securitySt',
  'channel',
  'authFailCount',
  'wirelessNetworkId',
  'deviceId',
  'nwName',
  'channelWidth',
  'mloSupport',
  'mldAddress',
  'partnerLink',
  'txRate',
  'rxRate',
  'orgName'
];

/** `?searchText=a%20b` -> `a b`. */
export const parseSearchText = (searchText) => {
  if (typeof searchText !== 'string' || searchText === '') return '';
  const value = searchText.replace(/^\?searchText=/, '');
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** '1' connected, '0' disconnected, 'all' both. '2' (the drawer's disconnected tab) matched nothing in legacy; read as disconnected. */
const toConnected = (isConnected) => {
  if (`${isConnected}` === '1') return true;
  if (`${isConnected}` === '0' || `${isConnected}` === '2') return false;
  return undefined;
};

/**
 * Query of one new-endpoint request. Column filters (`dataFilter`) are not sent: legacy v3 applied none, and the AP
 * page encodes its status and connection filters lossily ('All' and translated labels all become '0').
 */
export const buildClientsQuery = ({ isConnected, searchText, sortBy, sortDir }, page, size) => {
  const query = { page, size };
  const connected = toConnected(isConnected);
  if (connected !== undefined) query.connected = connected;
  const search = parseSearchText(searchText);
  if (search) query.search = search;
  const sortField = SORT_FIELDS[sortBy];
  if (sortField) {
    query.sortBy = sortField;
    query.sortDirection = `${sortDir}`.toLowerCase() === 'desc' ? 'desc' : 'asc';
  } else {
    Object.assign(query, LEGACY_DEFAULT_SORT);
  }
  return query;
};

const toText = (value) => (value === undefined || value === null ? undefined : `${value}`);

const toEpochMillis = (instant) => {
  const millis = Date.parse(instant);
  return Number.isNaN(millis) ? 0 : millis;
};

const toOptionalEpochMillis = (instant) => (instant === undefined || instant === null ? undefined : toEpochMillis(instant));

// '1'/'0', as mapRadioBand compares it.
const toFlag = (value) => (value === undefined || value === null ? undefined : value ? '1' : '0');

// Legacy rows say Allow / Block, and the Resume/Pause switch compares with 'Allow'.
const toLegacyAllowOrBlock = (value) => ({ allow: 'Allow', block: 'Block' })[`${value}`.toLowerCase()] ?? value;

// Legacy rows carry Yes / No, as the index does.
const toYesNo = (value) => (value === undefined || value === null ? undefined : value ? 'Yes' : 'No');

const toLegacyType = (clientType) => {
  if (clientType === 'wired') return 'Wired';
  if (clientType === 'wireless') return 'Wireless';
  return undefined;
};

/** New row -> legacy row; absent fields stay absent. */
export const toLegacyRow = (row) => {
  const wired = row.clientType === 'wired';
  return {
    serialNo: row.serialNo,
    // Legacy: deviceName is the attached device, clientName the client.
    deviceName: row.connDeviceName,
    // Client rename (PRJCBUGEN-66396), as legacy's rename-aware rows: the custom name (sent only while
    // CLIENT_RENAME is on) is the one name shown, and the client's own name is kept apart, '' when it reports
    // none, so the AP drawer's device-type icon never reads a custom name.
    clientName: row.customName || row.deviceName,
    reportedName: row.deviceName ?? '',
    isRandomizedMac: isRandomizedMac(row.mac),
    deviceType: row.connDeviceType,
    clientType: row.connDeviceType,
    deviceModel: row.connDeviceModel,
    mac: row.mac,
    ip: row.ip,
    platform: row.platform,
    isConnected: row.connected === undefined ? undefined : row.connected ? '1' : '0',
    port: toText(row.port),
    type: toLegacyType(row.clientType),
    allowOrBlock: toLegacyAllowOrBlock(row.allowOrBlock),
    clientModel: row.deviceModel,
    parentMac: row.parentMac,
    createdAt: toOptionalEpochMillis(row.createdAt),
    modifiedAt: toOptionalEpochMillis(row.modifiedAt),
    vlanId: toText(row.vlanId),
    userName: row.userName,
    nwName: row.nwName,
    connectionStarted: toEpochMillis(row.connStartedAt),
    connectionEnded: toEpochMillis(row.connEndedAt),
    rssi: wired ? NOT_APPLICABLE : toText(row.rssi),
    signalStrength: wired ? NOT_APPLICABLE : toText(row.signalStrength),
    // connBand is legacy's radioBand. Not also sent as connBand: that key flips mapRadioBand's branch and relabels band 3.
    radioBand: toText(row.connBand),
    isTriBand: toFlag(row.isTriBand),
    ssid: row.ssid,
    mode: row.mode,
    bssid: row.bssid,
    authentication: toText(row.authentication),
    authFailCount: toText(row.authFailCount),
    encryption: toText(row.encryption),
    securitySt: toText(row.securitySt),
    rxBytes: toText(row.rxBytes),
    txBytes: toText(row.txBytes),
    channel: toText(row.channel),
    wirelessNetworkId: row.wirelessNetworkId,
    deviceId: row.deviceId,
    channelWidth: row.channelWidth,
    mloSupport: toYesNo(row.mloSupport),
    mldAddress: row.mldAddress,
    partnerLink: row.partnerLink,
    txRate: row.txRate,
    rxRate: row.rxRate,
    orgName: row.orgName
  };
};

/** One response page -> the legacy envelope. */
export const toLegacyResponse = (body) => {
  const info = (body?.content || []).map(toLegacyRow);
  const total = body?.page?.totalElements ?? info.length;
  return {
    info,
    details: { count: total, totalCount: total, muiPaginationLimit: LEGACY_PAGE_SIZE }
  };
};

/** `callApi` is useApi, passed in to avoid an import cycle. */
export const fetchClientsFromNewAppcom = async (callApi, params, payload, excludeHeaders) => {
  const sort = payload?.data || {};
  const legacy = {
    isConnected: params.isConnected,
    searchText: params.searchText,
    sortBy: sort.sortBy,
    sortDir: sort.sortDir
  };
  const page = Math.max(0, Number(params.page) || 0);
  const body = await callApi(
    NEW_APPCOM_CLIENT_LIST_KEY,
    { networkId: params.networkId, serialNo: params.serialNo },
    { data: buildClientsQuery(legacy, page, LEGACY_PAGE_SIZE) },
    [],
    excludeHeaders
  );
  // useApi answers undefined for an expired session; callers rely on that to stop, so it is not an empty list.
  if (body === undefined) return undefined;
  return toLegacyResponse(body);
};
